import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Card, Button } from 'react-bootstrap';

import { useParams, Link } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

/* Per-status UI metadata (shared shape with My Tickets) */
const STATUS_META = {
  Open: { color: '#ef4444', bg: 'danger', icon: '🔴', step: 1 },
  'In Progress': { color: '#f59e0b', bg: 'warning', icon: '🛠️', step: 2 },
  Closed: { color: '#22c55e', bg: 'success', icon: '✅', step: 3 }
};

const metaFor = (status) => STATUS_META[status] || STATUS_META.Open;

const STEPS = ['Opened', 'In Progress', 'Closed'];

function TicketProgress({ status }) {
  const reached = metaFor(status).step;

  return (
    <div className="td-steps">
      {STEPS.map((label, i) => {
        const done = i + 1 <= reached;
        const current = i + 1 === reached;

        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <span className={`td-step-line ${done ? 'is-done' : ''}`} />
            )}
            <div className={`td-step ${done ? 'is-done' : ''} ${current ? 'is-current' : ''}`}>
              <span className="td-step-dot">{done ? '✓' : i + 1}</span>
              <span className="td-step-label">{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TicketDetailsPage() {
  const { id } = useParams();

  const [ticket, setTicket] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const { data } = await axios.get(
          `${API}/tickets/${id}`
        );

        setTicket(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [id]);

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="td-wrap">
          <Container style={{ maxWidth: '860px' }}>
            <div className="td-skel td-skel-hero mb-4" />
            <div className="td-skel td-skel-block mb-4" />
            <div className="td-skel td-skel-block" />
          </Container>
        </div>

        <style>{ticketDetailStyles}</style>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <CustomNavbar />

        <div className="td-wrap">
          <Container style={{ maxWidth: '860px' }}>
            <Card className="border-0 shadow-sm text-center py-5">
              <Card.Body>
                <div style={{ fontSize: '3rem' }}>🔍</div>
                <h5 className="fw-bold mt-3">Ticket Not Found</h5>
                <p className="text-muted mb-3">
                  This ticket may have been closed and removed.
                </p>
                <Button as={Link} to="/my-tickets" className="td-btn" size="sm">
                  ← Back to My Tickets
                </Button>
              </Card.Body>
            </Card>
          </Container>
        </div>

        <style>{ticketDetailStyles}</style>
      </>
    );
  }

  const meta = metaFor(ticket.status);

  return (
    <>
      <CustomNavbar />

      <div className="td-wrap">
        <Container style={{ maxWidth: '860px' }}>
          <Link to="/my-tickets" className="td-back">
            ← Back to My Tickets
          </Link>

          {/* Hero */}
          <Card
            className="border-0 shadow-lg mb-4 td-hero"
            style={{ '--td-accent': meta.color }}
          >
            <span className="td-hero-blob td-hero-blob-1" />
            <span className="td-hero-blob td-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <span className="td-ticket-id">
                    Ticket #{ticket._id.slice(-6)}
                  </span>
                  <h1 className="fw-bold mb-0 mt-2 td-subject">
                    {ticket.subject}
                  </h1>
                </div>
                <span className="td-status-pill">
                  {meta.icon} {ticket.status}
                </span>
              </div>

              <div className="td-progress-box">
                <TicketProgress status={ticket.status} />
              </div>
            </Card.Body>
          </Card>

          {/* Meta grid */}
          <Card className="border-0 shadow-sm mb-4 td-card">
            <Card.Body className="p-4">
              <div className="td-meta-grid">
                <div className="td-meta">
                  <div className="td-meta-label">📦 Product</div>
                  <div className="td-meta-value">
                    {ticket.productName || 'N/A'}
                  </div>
                </div>
                <div className="td-meta">
                  <div className="td-meta-label">🏷️ Category</div>
                  <div className="td-meta-value">{ticket.category}</div>
                </div>
                <div className="td-meta">
                  <div className="td-meta-label">✉️ Email</div>
                  <div className="td-meta-value">{ticket.email}</div>
                </div>
                <div className="td-meta">
                  <div className="td-meta-label">📅 Created</div>
                  <div className="td-meta-value">
                    {new Date(ticket.createdAt).toLocaleString()}
                  </div>
                </div>
                {ticket.orderId && (
                  <div className="td-meta">
                    <div className="td-meta-label">🧾 Order</div>
                    <div className="td-meta-value">{ticket.orderId}</div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Conversation */}
          <Card className="border-0 shadow-sm td-card">
            <Card.Body className="p-4 p-md-5">
              <h5 className="fw-bold mb-4">💬 Conversation</h5>

              {/* Customer message */}
              <div className="td-msg td-msg-user">
                <div className="td-avatar td-avatar-user">🙋</div>
                <div className="td-bubble">
                  <div className="td-bubble-head">
                    <span className="fw-bold">{ticket.name || 'You'}</span>
                    <small className="text-muted">
                      {new Date(ticket.createdAt).toLocaleString()}
                    </small>
                  </div>
                  <p className="mb-0 td-bubble-text">{ticket.message}</p>
                </div>
              </div>

              {/* Support reply */}
              {ticket.adminReply ? (
                <div className="td-msg td-msg-admin">
                  <div className="td-avatar td-avatar-admin">👨‍💼</div>
                  <div className="td-bubble td-bubble-admin">
                    <div className="td-bubble-head">
                      <span className="fw-bold">PoojaMart Support Team</span>
                      <span className="td-verified">✔ Official</span>
                    </div>
                    <p className="mb-0 td-bubble-text">{ticket.adminReply}</p>
                  </div>
                </div>
              ) : (
                <div className="td-pending">
                  <div className="td-pending-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div>
                    <div className="fw-bold">Support hasn't replied yet</div>
                    <small className="text-muted">
                      Our team is on it — you'll get a response here soon.
                    </small>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>

      <style>{ticketDetailStyles}</style>
    </>
  );
}

const ticketDetailStyles = `
  .td-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  .td-back {
    display: inline-block;
    margin-bottom: 16px;
    color: #7c2d12;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.2s ease;
  }
  .td-back:hover { transform: translateX(-4px); color: #9a3412; }

  /* Hero */
  .td-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, var(--td-accent));
    position: relative;
  }
  .td-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
    pointer-events: none;
  }
  .td-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 15%;
    animation: tdFloat 9s ease-in-out infinite;
  }
  .td-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
    animation: tdFloat 11s ease-in-out infinite reverse;
  }
  .td-ticket-id {
    display: inline-block;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.28);
    border-radius: 20px;
    padding: 4px 14px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .td-subject {
    font-size: clamp(1.5rem, 3.5vw, 2.2rem);
    line-height: 1.2;
  }
  .td-status-pill {
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.32);
    border-radius: 30px;
    padding: 8px 18px;
    font-weight: 700;
    white-space: nowrap;
  }
  .td-progress-box {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 16px;
    padding: 18px 20px 14px;
    backdrop-filter: blur(6px);
  }

  /* Cards */
  .td-card { border-radius: 18px; }

  /* Meta grid */
  .td-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
  .td-meta {
    background: #f8fafc;
    border-radius: 12px;
    padding: 12px 16px;
  }
  .td-meta-label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
    margin-bottom: 3px;
  }
  .td-meta-value {
    font-weight: 600;
    color: #1e293b;
    word-break: break-word;
  }
  @media (max-width: 575.98px) {
    .td-meta-grid { grid-template-columns: 1fr; }
  }

  /* Status progress rail (on dark hero) */
  .td-steps { display: flex; align-items: center; }
  .td-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
  }
  .td-step-dot {
    width: 30px; height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 700;
    background: rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.7);
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
  }
  .td-step.is-done .td-step-dot {
    background: #fff;
    color: var(--td-accent);
  }
  .td-step.is-current .td-step-dot {
    transform: scale(1.14);
    box-shadow: 0 0 0 5px rgba(255,255,255,0.22);
  }
  .td-step-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: rgba(255,255,255,0.65);
    white-space: nowrap;
  }
  .td-step.is-done .td-step-label { color: #fff; }
  .td-step-line {
    flex: 1 1 auto;
    height: 3px;
    border-radius: 3px;
    background: rgba(255,255,255,0.2);
    margin: 0 8px 24px;
    transition: background 0.3s ease;
  }
  .td-step-line.is-done { background: #fff; }

  /* Conversation bubbles */
  .td-msg {
    display: flex;
    gap: 14px;
    margin-bottom: 22px;
  }
  .td-msg:last-child { margin-bottom: 0; }
  .td-msg-admin { flex-direction: row-reverse; }
  .td-avatar {
    flex: 0 0 auto;
    width: 48px; height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 6px 14px rgba(0,0,0,0.12);
  }
  .td-avatar-user { background: linear-gradient(135deg, #ede9fe, #ddd6fe); }
  .td-avatar-admin { background: linear-gradient(135deg, #d1fae5, #a7f3d0); }
  .td-bubble {
    flex: 1 1 auto;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    border-top-left-radius: 4px;
    padding: 14px 18px;
  }
  .td-bubble-admin {
    background: #f0fdf4;
    border-color: #bbf7d0;
    border-top-left-radius: 16px;
    border-top-right-radius: 4px;
  }
  .td-bubble-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
  }
  .td-bubble-text {
    font-size: 1rem;
    line-height: 1.7;
    color: #334155;
    white-space: pre-wrap;
  }
  .td-verified {
    background: #16a34a;
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    border-radius: 20px;
    padding: 2px 10px;
  }

  /* Pending reply */
  .td-pending {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 16px;
    padding: 18px 20px;
  }
  .td-pending-dots { display: flex; gap: 6px; }
  .td-pending-dots span {
    width: 10px; height: 10px;
    border-radius: 50%;
    background: #f59e0b;
    animation: tdBlink 1.4s ease-in-out infinite;
  }
  .td-pending-dots span:nth-child(2) { animation-delay: 0.2s; }
  .td-pending-dots span:nth-child(3) { animation-delay: 0.4s; }

  .td-btn, .td-back.td-btn {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
  }

  @keyframes tdFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }
  @keyframes tdBlink {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1); }
  }

  /* Loading skeletons */
  .td-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: tdShimmer 1.4s ease infinite;
    border-radius: 18px;
  }
  .td-skel-hero { height: 200px; }
  .td-skel-block { height: 150px; }
  @keyframes tdShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .td-hero-blob, .td-pending-dots span, .td-skel { animation: none; }
  }
`;

export default TicketDetailsPage;
